import {
  contiguousWallFaceKeys,
  wallSelectionTiles,
  type FaceTile,
  type SceneCell,
  type ScenePlatform,
} from "@/lib/location-scene-3d/platform"

export type PlatformSector = { x: number; z: number }

export type PlatformSelectionKind = "none" | "sectors" | "wall" | "tiles"

export type PlatformSelectionState = {
  kind: PlatformSelectionKind
  sectors: PlatformSector[]
  tiles: FaceTile[]
  /** Last non-empty face tiles — survives marquee prelude empty. */
  baselineTiles: FaceTile[]
  /** Last non-empty sectors — survives marquee prelude empty. */
  baselineSectors: PlatformSector[]
  panelOpen: boolean
}

export type PlatformSelectionEvent =
  | { type: "clear" }
  | {
      type: "sectors"
      sectors: PlatformSector[]
      additive: boolean
      /** Marquee replace start — keep kind sectors; omit for intentional clear → none. */
      prelude?: boolean
    }
  | {
      type: "face"
      faceKey: string | null
      additive: boolean
      cells: ReadonlyArray<SceneCell>
      platform: ScenePlatform | null
    }
  | {
      type: "faceTiles"
      tiles: FaceTile[]
      additive: boolean
      cells: ReadonlyArray<SceneCell>
      platform: ScenePlatform | null
    }
  | { type: "replaceFaceTiles"; tiles: FaceTile[] }
  | { type: "filterFaceTiles"; keep: (tile: FaceTile) => boolean }

export const emptyPlatformSelection = (): PlatformSelectionState => ({
  kind: "none",
  sectors: [],
  tiles: [],
  baselineTiles: [],
  baselineSectors: [],
  panelOpen: false,
})

export function selectionSectors(state: PlatformSelectionState): PlatformSector[] {
  return state.kind === "sectors" ? state.sectors : []
}

export function selectionFaceTiles(state: PlatformSelectionState): FaceTile[] {
  return state.kind === "wall" || state.kind === "tiles" ? state.tiles : []
}

export function selectionSurfaceGrid(state: PlatformSelectionState): boolean {
  return state.kind === "tiles"
}

export function selectionToolMode(state: PlatformSelectionState): "sector" | "surface" | null {
  if (state.kind === "sectors") return "sector"
  if (state.kind === "wall" || state.kind === "tiles") return "surface"
  return null
}

export function selectionSurfaceSelect(state: PlatformSelectionState): "wall" | "tiles" {
  return state.kind === "tiles" ? "tiles" : "wall"
}

function withBaseline(tiles: FaceTile[], prev: PlatformSelectionState): FaceTile[] {
  return tiles.length > 0 ? tiles : prev.baselineTiles
}

function toNone(): PlatformSelectionState {
  return emptyPlatformSelection()
}

function toSectors(sectors: PlatformSector[], baseline?: PlatformSector[]): PlatformSelectionState {
  return {
    kind: "sectors",
    sectors,
    tiles: [],
    baselineTiles: [],
    baselineSectors: sectors.length > 0 ? sectors : (baseline ?? []),
    panelOpen: false,
  }
}

function toWall(tiles: FaceTile[], panelOpen = true): PlatformSelectionState {
  if (tiles.length === 0) return toNone()
  return {
    kind: "wall",
    sectors: [],
    tiles,
    baselineTiles: tiles,
    baselineSectors: [],
    panelOpen,
  }
}

function toTiles(tiles: FaceTile[], baseline: FaceTile[], panelOpen = true): PlatformSelectionState {
  return {
    kind: "tiles",
    sectors: [],
    tiles,
    baselineTiles: tiles.length > 0 ? tiles : baseline,
    baselineSectors: [],
    panelOpen,
  }
}

function runKeysForFace(faceKey: string, cells: ReadonlyArray<SceneCell>, platform: ScenePlatform): Set<string> {
  const run = contiguousWallFaceKeys(faceKey, cells, platform)
  return new Set(run.length > 0 ? run : [faceKey])
}

function wallTilesForFace(faceKey: string, cells: ReadonlyArray<SceneCell>, platform: ScenePlatform): FaceTile[] {
  const all = wallSelectionTiles(faceKey, cells, platform)
  return all.length > 0 ? all : [{ faceKey, u: 0, v: 0 }]
}

function facesOverlap(groupKeys: Set<string>, tiles: FaceTile[]): boolean {
  const keys = new Set(tiles.map((tile) => tile.faceKey))
  return [...groupKeys].some((key) => keys.has(key))
}

function mergeSectors(current: PlatformSector[], free: PlatformSector[], additive: boolean): PlatformSector[] {
  if (!additive) return free
  if (free.length === 1) {
    const key = `${free[0].x}:${free[0].z}`
    if (current.some((sector) => `${sector.x}:${sector.z}` === key)) {
      return current.filter((sector) => `${sector.x}:${sector.z}` !== key)
    }
    return [...current, free[0]]
  }
  const keys = new Set(current.map((sector) => `${sector.x}:${sector.z}`))
  const next = [...current]
  for (const sector of free) {
    const key = `${sector.x}:${sector.z}`
    if (keys.has(key)) continue
    keys.add(key)
    next.push(sector)
  }
  return next
}

function reduceSectors(
  state: PlatformSelectionState,
  event: Extract<PlatformSelectionEvent, { type: "sectors" }>,
): PlatformSelectionState {
  if (event.sectors.length === 0 && !event.additive) {
    // Marquee prelude only — intentional clear must reach toNone.
    if (event.prelude && state.kind === "sectors") {
      return toSectors([], state.baselineSectors.length > 0 ? state.baselineSectors : state.sectors)
    }
    return toNone()
  }
  if (event.sectors.length === 0) return state
  const current = state.kind === "sectors" ? state.sectors : []
  const next = mergeSectors(current, event.sectors, event.additive)
  if (next.length === 0) return toNone()
  return toSectors(next)
}

function reduceFace(
  state: PlatformSelectionState,
  event: Extract<PlatformSelectionEvent, { type: "face" }>,
): PlatformSelectionState {
  if (!event.faceKey) return toNone()
  const { faceKey, additive, cells, platform } = event

  if (state.kind === "tiles") {
    // Face without UV while in tiles: treat as wall expand of that key.
    if (!platform) {
      const seed = { faceKey, u: 0, v: 0 }
      if (!additive) return toWall([seed])
      const current = state.tiles
      if (current.some((tile) => tile.faceKey === faceKey)) {
        const next = current.filter((tile) => tile.faceKey !== faceKey)
        return next.length === 0 ? toNone() : toTiles(next, next)
      }
      return toTiles([...current, seed], [...current, seed])
    }
  }

  if (platform && (state.kind === "none" || state.kind === "sectors" || state.kind === "wall")) {
    const tiles = wallTilesForFace(faceKey, cells, platform)
    const groupKeys = runKeysForFace(faceKey, cells, platform)
    if (!additive) return toWall(tiles)
    if (state.kind !== "wall") return toWall(tiles)
    const overlap = facesOverlap(groupKeys, state.tiles)
    if (overlap) {
      const next = state.tiles.filter((tile) => !groupKeys.has(tile.faceKey))
      return next.length === 0 ? toNone() : toWall(next)
    }
    return toWall([...state.tiles, ...tiles])
  }

  // No platform or tiles-mode seed path without platform expand.
  const seed = { faceKey, u: 0, v: 0 }
  if (!additive) {
    if (state.kind === "tiles" && state.tiles.some((tile) => tile.faceKey === faceKey)) return state
    return toWall([seed])
  }
  const current = state.kind === "wall" || state.kind === "tiles" ? state.tiles : []
  if (current.some((tile) => tile.faceKey === faceKey)) {
    const next = current.filter((tile) => tile.faceKey !== faceKey)
    return next.length === 0 ? toNone() : toWall(next)
  }
  return toWall([...current, seed])
}

function reduceFaceTiles(
  state: PlatformSelectionState,
  event: Extract<PlatformSelectionEvent, { type: "faceTiles" }>,
): PlatformSelectionState {
  const { tiles, additive, cells, platform } = event

  if (tiles.length === 0 && !additive) {
    // Marquee prelude: keep tiles kind + baseline for "other wall" promote.
    if (state.kind === "tiles") return toTiles([], state.baselineTiles, true)
    return toNone()
  }

  if (!platform || tiles.length === 0) {
    if (!additive) return tiles.length === 0 ? toNone() : toTiles(tiles, tiles)
    return state
  }

  const faceKey = tiles[0].faceKey
  const groupKeys = runKeysForFace(faceKey, cells, platform)
  const wallAll = wallTilesForFace(faceKey, cells, platform)

  // Wall kind (or coming from sectors/none via tile hit): expand / drill / additive.
  if (state.kind === "wall" || state.kind === "none" || state.kind === "sectors") {
    const currentTiles = state.kind === "wall" ? state.tiles : []
    const overlap = facesOverlap(groupKeys, currentTiles)
    if (!additive && overlap && tiles.length === 1 && state.kind === "wall") {
      return toTiles([tiles[0]], [tiles[0]])
    }
    if (!additive) return toWall(wallAll)
    if (state.kind !== "wall") return toWall(wallAll)
    if (overlap) {
      const next = currentTiles.filter((tile) => !groupKeys.has(tile.faceKey))
      return next.length === 0 ? toNone() : toWall(next)
    }
    return toWall([...currentTiles, ...wallAll])
  }

  // Tiles kind.
  if (!additive) {
    const baseline = withBaseline(state.tiles, state)
    if (baseline.length > 0) {
      const overlap = facesOverlap(groupKeys, baseline)
      if (!overlap) return toWall(wallAll)
    }
    return toTiles(tiles, tiles.length > 0 ? tiles : state.baselineTiles)
  }

  if (tiles.length === 1) {
    const key = `${tiles[0].faceKey}:${tiles[0].u}:${tiles[0].v}`
    if (state.tiles.some((tile) => `${tile.faceKey}:${tile.u}:${tile.v}` === key)) {
      const next = state.tiles.filter((tile) => `${tile.faceKey}:${tile.u}:${tile.v}` !== key)
      return next.length === 0 ? toNone() : toTiles(next, next)
    }
    const next = [...state.tiles, tiles[0]]
    return toTiles(next, next)
  }

  const keys = new Set(state.tiles.map((tile) => `${tile.faceKey}:${tile.u}:${tile.v}`))
  const next = [...state.tiles]
  for (const tile of tiles) {
    const key = `${tile.faceKey}:${tile.u}:${tile.v}`
    if (keys.has(key)) continue
    keys.add(key)
    next.push(tile)
  }
  return toTiles(next, next)
}

export function reducePlatformSelection(
  state: PlatformSelectionState,
  event: PlatformSelectionEvent,
): PlatformSelectionState {
  switch (event.type) {
    case "clear":
      return toNone()
    case "sectors":
      return reduceSectors(state, event)
    case "face":
      return reduceFace(state, event)
    case "faceTiles":
      return reduceFaceTiles(state, event)
    case "replaceFaceTiles": {
      if (event.tiles.length === 0) return toNone()
      if (state.kind === "tiles") return toTiles(event.tiles, event.tiles, state.panelOpen)
      return toWall(event.tiles, state.panelOpen || true)
    }
    case "filterFaceTiles": {
      if (state.kind !== "wall" && state.kind !== "tiles") return state
      const next = state.tiles.filter(event.keep)
      if (next.length === 0) return toNone()
      return state.kind === "tiles" ? toTiles(next, next, state.panelOpen) : toWall(next, state.panelOpen)
    }
    default:
      return state
  }
}
